import {
  ApplicationConfig,
  APP_INITIALIZER,
  ErrorHandler,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withPreloading } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { Overlay } from '@angular/cdk/overlay';
import { MAT_SELECT_SCROLL_STRATEGY, MAT_SELECT_CONFIG } from '@angular/material/select';
import * as Sentry from '@sentry/angular';
import { SelectivePreloadStrategy } from './core/selective-preload.strategy';
import { authInterceptorFn } from './core/interceptors/auth.interceptor';
import { cacheInterceptorFn } from './core/interceptors/cache.interceptor';
import { retryInterceptorFn } from './core/interceptors/retry.interceptor';
import { APP_ROUTES } from './app.routes';
import { environment } from 'src/environments/environment';
import { WebVitalsService } from './core/services/web-vitals.service';

export function scrollStrategyFactory(overlay: Overlay) {
  return () => overlay.scrollStrategies.reposition({ autoClose: false });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(APP_ROUTES, withPreloading(SelectivePreloadStrategy)),
    provideHttpClient(
      withInterceptors([cacheInterceptorFn, authInterceptorFn, retryInterceptorFn]),
    ),
    provideAnimationsAsync(),
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      useFactory: scrollStrategyFactory,
      deps: [Overlay],
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { disableOptionCentering: true, overlayPanelClass: 'pay-fee-panel' },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (webVitals: WebVitalsService) => () => {
        if (environment.production) {
          if (!environment.sentryDsn || environment.sentryDsn.startsWith('__')) {
            console.error('[Sentry] DSN not configured — error monitoring disabled in production');
            return;
          }
          Sentry.init({
            dsn: environment.sentryDsn,
            environment: 'production',
            release: environment.sentryRelease,
            tracesSampleRate: 0.05,
            integrations: [Sentry.browserTracingIntegration()],
          });
          webVitals.report();
        }
      },
      deps: [WebVitalsService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      // Passthrough loader: lets NgOptimizedImage work with arbitrary backend URLs.
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => config.src,
    },
  ],
};
